import { io, Socket } from 'socket.io-client';
import { firstValueFrom, Observable, take } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import {
  ChannelCreated,
  ChannelJoined,
  ChatType,
  CreateChannel,
  CreateNewMessage,
  JoinChannel,
  LeaveChannel,
  MessageReactedEvent,
  MessageReactedTo,
  MessageSentTo,
  NewMessageSent,
  ReactToMessage,
  ReactToMessageRequest,
  SendMessage,
} from '@smatchy/chat-domain';

interface ChatSDKOptions {
  serverUrl: string;
}

export interface ChatMessage {
  id: string;
  text: string;
  sender?: string;
  timestamp?: number;
}

interface UserClient {
  userId: string;
  token: string;
}

export class Channel {
  #socket: Socket;
  #id: string;
  #type: ChatType;

  get id(): string {
    return this.#id;
  }

  /**
   * The server keeps each conversation in a socket.io room, and a room does
   * not survive a reconnection (network lost, app back from the background).
   * Joining again on every reconnection keeps messages coming.
   */
  readonly #rejoin = () => {
    this.#socket.emit(JoinChannel, { id: this.#id });
  };

  constructor(socket: Socket, id: string, type: ChatType, participantIds: string[], initiatorId: string) {
    this.#id = id;
    this.#type = type;
    this.#socket = socket;
    if (id && socket) {
      socket.on('connect', this.#rejoin);
    }
  }

  sendMessage(text: string): void {
    const createNewMessage: CreateNewMessage = {
      chatId: this.#id,
      type: this.#type,
      messageType: 'text',
      messageData: {
        text,
      },
    };
    this.#socket.emit(SendMessage, createNewMessage);
  }

  message(): Observable<NewMessageSent> {
    console.log('subscribed to ', MessageSentTo(this.#id));
    return this.onEvent<NewMessageSent>(MessageSentTo(this.#id)).pipe(map((message: NewMessageSent) => message));
  }

  /** SMA-22: picks, replaces or takes back this person's reaction. */
  react(messageId: string, emoji: string): void {
    const request: ReactToMessageRequest = { chatId: this.#id, messageId, emoji };
    this.#socket.emit(ReactToMessage, request);
  }

  reactions(): Observable<MessageReactedEvent> {
    return this.onEvent<MessageReactedEvent>(MessageReactedTo(this.#id));
  }

  leaveChannel() {
    this.#socket.off('connect', this.#rejoin);
    this.#socket.emit(LeaveChannel, {
      id: this.id,
    });
  }

  private onceEvent<T>(event: string): Observable<T> {
    return new Observable<T>((observer) => {
      this.#socket.once(event, (data: T) => observer.next(data));
    }).pipe(filter((data) => data !== undefined));
  }

  private onEvent<T>(event: string): Observable<T> {
    return new Observable<T>((observer) => {
      this.#socket.on(event, (data: T) => observer.next(data));
    }).pipe(filter((data) => data !== undefined));
  }
}

export class ChatClient {
  private socket!: Socket;
  #user: UserClient | undefined;
  #url: string;
  #channel: Channel | undefined;

  get isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  get channel(): Channel {
    return this.#channel || new Channel(this.socket, '', 'group', [], '');
  }

  constructor(options: ChatSDKOptions) {
    this.#url = options.serverUrl;
  }

  connect(user: { userId: string; token: string }) {
    const previous = this.#user;
    this.#user = user;

    // The same person again, typically after the access token was refreshed.
    // The socket is kept: an open conversation holds it, with its listeners,
    // and a new socket used to leave that conversation deaf and mute until it
    // was reopened. Only the token changes, and the socket reconnects with it;
    // the open channel then joins its room again on 'connect'.
    if (this.socket && previous?.userId === user.userId) {
      if (previous.token === user.token && this.socket.active) {
        return;
      }
      this.socket.io.opts.extraHeaders = { Authorization: `${user.token}` };
      this.socket.disconnect();
      this.socket.connect();
      return;
    }

    // Someone else, or the first connection: start from a clean socket.
    this.disconnect();

    this.socket = io(this.#url, {
      withCredentials: true,
      extraHeaders: {
        Authorization: `${user.token}`,
      },
    });
  }

  disconnect() {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
    }
  }

  createChannel(type: ChatType, id: string): Promise<Channel> {
    this.socket.emit(CreateChannel, { name: id, type, id });
    return firstValueFrom(
      this.onceEvent<{
        _id: string;
        type: ChatType;
        participantIds: string[];
        // GroupId / EventId /
        refId: string;
        initiatorId: string;
      }>(ChannelCreated).pipe(
        take(1),
        map(
          (channel: {
            _id: string;
            type: ChatType;
            participantIds: string[];
            // GroupId / EventId /
            refId: string;
            initiatorId: string;
          }) => {
            return new Channel(this.socket, channel._id, channel.type, channel.participantIds, channel.initiatorId);
          },
        ),
      ),
    );
  }

  joinChannel(channelId: string): Promise<Channel> {
    this.socket.emit(JoinChannel, { id: channelId });
    return firstValueFrom(
      this.onceEvent<{
        _id: string;
        type: ChatType;
        participantIds: string[];
        // GroupId / EventId /
        refId: string;
        initiatorId: string;
      }>(ChannelJoined).pipe(
        take(1),
        map((channel: { _id: string; type: ChatType; participantIds: string[]; initiatorId: string }) => {
          return new Channel(this.socket, channel._id, channel.type, channel.participantIds, channel.initiatorId);
        }),
      ),
    );
  }

  private onEvent<T>(event: string): Observable<T> {
    return new Observable<T>((observer) => {
      this.socket.on(event, (data: T) => observer.next(data));
    }).pipe(filter((data) => data !== undefined));
  }

  private onceEvent<T>(event: string): Observable<T> {
    return new Observable<T>((observer) => {
      this.socket.once(event, (data: T) => observer.next(data));
    }).pipe(filter((data) => data !== undefined));
  }
}
